import type { ApiErrorResponse } from "@shop-ledger/shared-types";
import { API_BASE_URL } from "../env";

/**
 * Thin authenticated-fetch wrapper shared by every `api/*Api.ts` module in this app.
 * Every domain module builds on this one function rather than each hand-rolling its own
 * `fetch` + header + error-parsing boilerplate (the pattern `pos-pwa/src/auth/authApi.ts`
 * already established, generalized here since this app calls many more route groups).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function parseErrorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ApiErrorResponse;
    if (typeof body.detail === "string") return body.detail;
    if (Array.isArray(body.detail)) return body.detail.map((item) => `${item.loc.join(".")}: ${item.msg}`).join("; ");
  } catch {
    // Response body wasn't JSON — fall through to the generic message below.
  }
  return `Request failed with status ${response.status}`;
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  accessToken?: string | null;
  /** For multipart uploads — bypasses the JSON body/Content-Type handling below. */
  formData?: FormData;
}

function buildUrl(path: string, query?: ApiRequestOptions["query"]): string {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Every tenant-scoped route in this backend derives `tenant_id` from the bearer token —
 * never send a tenant id in a header or body field (`backend/BACKEND_ARCHITECTURE.md`'s
 * API surface table). This wrapper only ever attaches `Authorization`.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, accessToken, formData } = options;

  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (!formData && body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });

  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
